import {
  QUANT_BITS,
  QUANT_QUALITY_PENALTY,
  RUNTIMES,
  findAppleChip,
  findGpu,
  findRuntime,
} from './catalog.js';
import type {
  CodingTier,
  FitEstimate,
  FitVerdict,
  MachineProfile,
  ModelProfile,
  Quantization,
  RuntimeId,
} from './types.js';

const KV_CACHE_BASELINE_GB = 0.5;
const WEIGHT_OVERHEAD = 1.08;

export function estimateWeightsGb(paramsB: number, quant: Quantization): number {
  const bits = QUANT_BITS[quant];
  return (paramsB * bits) / 8;
}

export function estimateRequiredGb(paramsB: number, quant: Quantization): number {
  return estimateWeightsGb(paramsB, quant) * WEIGHT_OVERHEAD + KV_CACHE_BASELINE_GB;
}

function bandwidthGbps(machine: MachineProfile): number {
  if (machine.accel === 'apple-silicon' && machine.appleChipId) {
    return findAppleChip(machine.appleChipId)?.memoryBandwidthGbps ?? 100;
  }
  if (machine.gpuId) {
    return findGpu(machine.gpuId)?.memoryBandwidthGbps ?? 300;
  }
  return 50;
}

function computeTps(machine: MachineProfile): number {
  if (machine.accel === 'apple-silicon' && machine.appleChipId) {
    const chip = findAppleChip(machine.appleChipId);
    if (!chip) return 8;
    const tierMultiplier: Record<string, number> = { base: 1, pro: 1.6, max: 2.4, ultra: 3.0 };
    const generationMultiplier: Record<string, number> = { m1: 0.7, m2: 0.85, m3: 1.0, m4: 1.15 };
    return 12 * (tierMultiplier[chip.tier] ?? 1) * (generationMultiplier[chip.generation] ?? 1);
  }
  if (machine.gpuId) {
    return findGpu(machine.gpuId)?.computeTierTps ?? 25;
  }
  return 5;
}

function runtimeEfficiency(runtimeId: RuntimeId, accel: MachineProfile['accel']): number {
  const runtime = findRuntime(runtimeId);
  if (!runtime) return 0.7;
  if (!runtime.accelerators.includes(accel)) return 0.4;
  if (runtimeId === 'mlx' && accel === 'apple-silicon') return 1.12;
  if (runtimeId === 'llama-cpp') return 1.05;
  if (runtimeId === 'vllm' && accel === 'cuda') return 1.15;
  if (runtimeId === 'ollama') return 1.0;
  if (runtimeId === 'lm-studio') return 0.94;
  return 0.9;
}

function classifyVerdict(headroomRatio: number, expectedTps: number, tier: CodingTier): FitVerdict {
  if (headroomRatio < 0.98) return 'no';
  if (headroomRatio < 1.08) return 'technical';
  if (expectedTps < 3) return 'chat-only';
  if (headroomRatio < 1.35) return 'usable';
  if (expectedTps < 12) return 'good';
  if (tier === 'S' || tier === 'A') return 'great';
  return 'good';
}

export function verdictHeadline(v: FitVerdict): string {
  switch (v) {
    case 'great':
      return 'Great fit for local coding';
    case 'good':
      return 'Good for day-to-day coding';
    case 'usable':
      return 'Usable, but slow for multi-file work';
    case 'chat-only':
      return 'Fine for chat, weak for coding';
    case 'technical':
      return 'Technically runs — not worth it';
    case 'no':
      return 'Not recommended on this machine';
  }
}

function codingUsefulnessTier(baseTier: CodingTier, quant: Quantization, verdict: FitVerdict): CodingTier {
  const order: CodingTier[] = ['S', 'A', 'B', 'C', 'D'];
  let idx = order.indexOf(baseTier);
  const penalty = QUANT_QUALITY_PENALTY[quant];
  if (penalty >= 8) idx += 2;
  else if (penalty >= 3) idx += 1;
  if (verdict === 'usable' || verdict === 'chat-only') idx += 1;
  if (verdict === 'technical' || verdict === 'no') idx += 2;
  return order[Math.min(idx, order.length - 1)] ?? 'D';
}

export function estimateFit(
  machine: MachineProfile,
  model: ModelProfile,
  quant: Quantization,
  runtime: RuntimeId,
): FitEstimate {
  const requiredGb = estimateRequiredGb(model.paramsB, quant);
  const available = machine.accel === 'apple-silicon' ? machine.vramGb : machine.vramGb;
  const headroomRatio = available / requiredGb;

  const baseTps = computeTps(machine);
  const bw = bandwidthGbps(machine);
  const weightsGb = estimateWeightsGb(model.paramsB, quant);

  const memoryBoundTps = bw / (weightsGb * 1.05);
  const computeBoundTps = baseTps * (Math.log2(1 + 12 / Math.max(1, model.paramsB)) / 2 + 0.5);
  const runtimeFactor = runtimeEfficiency(runtime, machine.accel);
  const rawTps = Math.min(memoryBoundTps, computeBoundTps) * runtimeFactor;
  const expectedTps = Math.max(0.5, Math.round(rawTps * 10) / 10);

  const expectedTtftMs = Math.round(120 + (model.paramsB / baseTps) * 200 + (1 / runtimeFactor) * 80);
  const verdict = classifyVerdict(headroomRatio, expectedTps, model.codingTier);
  const tier = codingUsefulnessTier(model.codingTier, quant, verdict);

  const cautions: string[] = [];
  if (headroomRatio < 1.15) {
    cautions.push('Tight memory headroom — background apps may push it into swap.');
  }
  if (QUANT_QUALITY_PENALTY[quant] >= 6) {
    cautions.push('Aggressive quantization noticeably hurts code quality on long edits.');
  }
  if (!RUNTIMES.find((r) => r.id === runtime)?.accelerators.includes(machine.accel)) {
    cautions.push(`${runtime} does not accelerate on this machine — expect CPU-only fallback.`);
  }
  if (expectedTps < 6 && model.codingTier !== 'D') {
    cautions.push('Too slow for agent workflows that chain many tool calls.');
  }

  const explanation = buildExplanation(machine, model, quant, requiredGb, available, expectedTps);

  const confidence: FitEstimate['confidence'] =
    machine.accel === 'apple-silicon' || machine.accel === 'cuda' ? 'high' : 'medium';

  return {
    machineId: machine.id,
    modelId: model.id,
    quantization: quant,
    runtime,
    verdict,
    verdictHeadline: verdictHeadline(verdict),
    explanation,
    requiredGb: Math.round(requiredGb * 10) / 10,
    headroomRatio: Math.round(headroomRatio * 100) / 100,
    expectedTps,
    expectedTtftMs,
    expectedContext: Math.min(model.contextMax, Math.round(available * 1024 * 0.2)),
    codingUsefulnessTier: tier,
    confidence,
    cautions,
  };
}

function buildExplanation(
  machine: MachineProfile,
  model: ModelProfile,
  quant: Quantization,
  requiredGb: number,
  availableGb: number,
  tps: number,
): string {
  const headroom = (availableGb - requiredGb).toFixed(1);
  const gbUsed = requiredGb.toFixed(1);
  return (
    `${model.displayName} at ${quant} needs about ${gbUsed}GB. Your ${machine.nickname} ` +
    `has ~${availableGb.toFixed(0)}GB usable for inference, leaving ${headroom}GB headroom. ` +
    `Expect around ${tps} tokens/sec.`
  );
}

export function recommendQuantForMachine(machine: MachineProfile, model: ModelProfile): Quantization {
  const available = machine.vramGb;
  const preferred: Quantization[] = ['fp16', 'q8_0', 'q6_k', 'q5_k_m', 'q4_k_m', 'q3_k_m', 'q2_k'];
  for (const q of preferred) {
    if (!model.quantizations.includes(q)) continue;
    const required = estimateRequiredGb(model.paramsB, q);
    if (available / required >= 1.25) return q;
  }
  const fallback = model.quantizations[model.quantizations.length - 1];
  return fallback ?? 'q4_k_m';
}

export function recommendRuntimeForMachine(machine: MachineProfile): RuntimeId {
  if (machine.preferredRuntime) return machine.preferredRuntime;
  if (machine.accel === 'apple-silicon') {
    if (machine.installedRuntimes.includes('mlx')) return 'mlx';
    if (machine.installedRuntimes.includes('ollama')) return 'ollama';
    return 'ollama';
  }
  if (machine.accel === 'cuda') {
    if (machine.installedRuntimes.includes('llama-cpp')) return 'llama-cpp';
    if (machine.installedRuntimes.includes('ollama')) return 'ollama';
    return 'ollama';
  }
  return 'ollama';
}

export function scoreFit(fit: FitEstimate, model: ModelProfile): number {
  const verdictWeight: Record<FitVerdict, number> = {
    great: 1.0,
    good: 0.85,
    usable: 0.55,
    'chat-only': 0.3,
    technical: 0.15,
    no: 0,
  };
  const quality = Math.max(0, model.baseQualityScore - QUANT_QUALITY_PENALTY[fit.quantization]);
  const speedScore = Math.min(1, fit.expectedTps / 40);
  return (
    quality * 0.55 * verdictWeight[fit.verdict] +
    speedScore * 35 * verdictWeight[fit.verdict] +
    (fit.headroomRatio > 1.5 ? 5 : 0)
  );
}
